import {
  useCheckDuplicatePatientIdentifiers,
  useDuplicatePatientIdentifiers,
} from '@/modules/patients/api/use-duplicate-patient-identifiers'
import {DuplicatePatientNotice} from '@/modules/patients/components/duplicate-patient-notice'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {DateValue, parseDate} from '@ark-ui/react/date-picker'
import {Box, Button, ButtonGroup, useDialogContext, VStack} from '@chakra-ui/react'
import {normalizePatientIdentifier} from '@cypro/engine/utils'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import * as z from 'zod'
import {useGetInsuranceCompanies} from '../../api/use-get-insurance-companies'
import {mapInsuranceCompaniesToSelectItems} from '@/modules/basic-data/functions'

type Insurance = {
  id?: string
  country?: string | null
  companyId?: string | null
  insuranceNumber?: string | null
  policyNumber?: string | null
  expirationDate?: number | null
  privatePatient?: boolean | null
}

const InsuranceSchema = z.object({
  country: z.string(),
  companyId: z.union([z.uuid(), z.literal('')]),
  insuranceNumber: z.string(),
  policyNumber: z.string(),
  expirationDate: z.any(),
  privatePatient: z.boolean(),
})

export function InsuranceDetailsDialog({patientId, insurance}: {patientId: string; insurance?: Insurance}) {
  const {t} = useTranslate('common')
  const zero = useZero()
  const context = useDialogContext()
  const checkDuplicateIdentifiers = useCheckDuplicatePatientIdentifiers()

  const [companies] = useGetInsuranceCompanies()
  const companyItems = mapInsuranceCompaniesToSelectItems(companies)

  const expirationDateDefault: DateValue[] = (() => {
    if (!insurance?.expirationDate) return []
    const d = new Date(insurance?.expirationDate)
    const yyyy = d.getUTCFullYear()
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    const dd = String(d.getUTCDate()).padStart(2, '0')
    return [parseDate(`${yyyy}-${mm}-${dd}`)]
  })()

  const form = useAppForm({
    defaultValues: {
      country: insurance?.country || '',
      companyId: insurance?.companyId || '',
      insuranceNumber: insurance?.insuranceNumber || '',
      policyNumber: insurance?.policyNumber || '',
      expirationDate: expirationDateDefault,
      privatePatient: Boolean(insurance?.privatePatient),
    },
    validators: {
      onSubmit: InsuranceSchema,
    },
    async onSubmit({value}) {
      const duplicateField = await checkDuplicateIdentifiers(changedIdentifiers(value.insuranceNumber), patientId)
      if (duplicateField) return

      return zero.attemptMutation(
        zero.mutators.patients.updateInsurance({
          patientId: patientId,
          country: value.country,
          companyId: value.companyId || undefined,
          insuranceNumber: value.insuranceNumber,
          policyNumber: value.policyNumber,
          expirationDate: value.expirationDate[0]?.toDate('UTC')?.getTime(),
          privatePatient: value.privatePatient,
        }),
        async mutation => {
          const data = await mutation.server
          if (!data) return
          toaster.success({title: t('success'), description: t('patient_updated_successfully')})
          onClose()
        }
      )
    },
  })

  // Only a changed number is checked, so a pre-existing duplicate doesn't block unrelated edits.
  function changedIdentifiers(insuranceNumber: string) {
    const changed =
      normalizePatientIdentifier(insuranceNumber) !== normalizePatientIdentifier(insurance?.insuranceNumber)
    return {insuranceNumber: changed ? insuranceNumber : undefined}
  }

  const duplicates = useDuplicatePatientIdentifiers(
    changedIdentifiers(useStore(form.store, state => state.values.insuranceNumber)),
    {excludePatientId: patientId}
  )

  function onClose() {
    context.setOpen(false)
  }

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('insurance_details')}</DialogHeader>

        <DialogBody minHeight="none" overflow="auto">
          <VStack gap="5" width="100%">
            <form.AppField name="country">
              {field => <field.TextField label={t('country')} placeholder={t('country_placeholder')} />}
            </form.AppField>

            <form.AppField name="companyId" mode="value">
              {field => (
                <field.Combobox label={t('company')} items={companyItems} placeholder={t('company_placeholder')} />
              )}
            </form.AppField>

            <form.AppField name="insuranceNumber">
              {field => (
                <Box width="100%">
                  <field.TextField
                    label={t('insurance_number')}
                    placeholder={t('insurance_number_placeholder')}
                    aria-invalid={Boolean(duplicates.insuranceNumber)}
                  />
                  <DuplicatePatientNotice patient={duplicates.insuranceNumber} />
                </Box>
              )}
            </form.AppField>

            <form.AppField name="policyNumber">
              {field => <field.TextField label={t('policy_number')} placeholder="e.g. 12 345678 B 901" />}
            </form.AppField>

            <form.AppField name="expirationDate">
              {field => <field.DatePicker label={t('expiration_date')} />}
            </form.AppField>

            <form.AppField name="privatePatient">
              {field => <field.Switch label={t('private_patient')} />}
            </form.AppField>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('cancel')}
            </Button>
            <form.SubmitButton disabled={isDefaultValue}>{t('save')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}
